import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormHelperText,
  Divider,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  Button,
  Link,
  IconButton,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ManualIcon from "src/assets/svg-icons/manual.svg";
import FoodicsIcon from "src/assets/svg-icons/foodics.svg";
import ExcelIcon from "src/assets/svg-icons/excel.svg";
import FileUploader from "src/shared/components/file-uploader";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useDeleteJourneyFile,
  useGetJourneyFiles,
  useUploadJourneyFileMutation,
} from "src/api/add-sales";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import {
  convertObjectToFormData,
  downloadCsvTemplate,
  formatBytes,
  formatNumber,
} from "src/utils";
import UploadedFile from "./uploaded-file";
import { FormData } from "../../constants";
import { IPaymentData, JourneyTypes, FormModeTypes } from "src/types/generic";
import DateRangeInput from "src/shared/components/date-range";
import moment from "moment";
import CustomRadioGroup from "src/shared/components/custom-radio-group";
import YoutubeViewerBox from "../../youtube-viewer-box";
import InfoBox from "src/shared/components/info-box";
import { useTranslation } from "react-i18next";
import OtherSales from "./other-sales";
import { foodicsTypes } from "src/types/add-sales";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import AddMoreIcon from "src/assets/svg-icons/settings/add_more.svg";
import { AWS_URLS } from "src/constants/aws-urls";

type filesTypes =
  | "orders"
  | "order_items"
  | "order_payments"
  | "product_sales"
  | "sale_payments";

interface IDateRange {
  start_date: string;
  end_date: string;
}

const CalculateSales = ({
  control,
  errors,
  watch,
  setValue,
  newPaymentsAggFields,
  appendNewPaymentsAgg,
  removeNewPaymentsAgg,
  newPayments,
  mode,
  journeyTaxPercentage,
  journeyTaxDivider,
  journeysDateRange,
  setFilesChanged,
  filesChanged,
  otherRevenueFields,
  appendOtherRevenue,
  removeOtherRevenue,
  paymentsList,
  isAutoImportedJourney,
  foodicsType,
  setFoodicsType,
  discountPaymentsFields,
  appendDiscountPayment,
  removeDiscountPayment,
}: {
  control: Control<FormData, any>;
  errors: FieldErrors<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  newPaymentsAggFields: any;
  appendNewPaymentsAgg: any;
  removeNewPaymentsAgg: any;
  newPayments: any[];
  mode: FormModeTypes;
  journeyTaxPercentage: number;
  journeyTaxDivider: number;
  journeysDateRange: IDateRange | undefined;
  setFilesChanged: Function;
  filesChanged: boolean;
  otherRevenueFields: any;
  appendOtherRevenue: any;
  removeOtherRevenue: any;
  paymentsList: IPaymentData[];
  isAutoImportedJourney: boolean;
  foodicsType: foodicsTypes;
  setFoodicsType: Function;
  discountPaymentsFields: any;
  appendDiscountPayment: any;
  removeDiscountPayment: any;
}) => {
  let [searchParams] = useSearchParams();
  const { id } = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [orderItemsFile, setOrderItemsFile] = useState<any>();
  const [orderPaymentsFile, setOrderPaymentsFile] = useState<any>();
  const [ordersFile, setOrdersFile] = useState<any>();
  const [productSalesFile, setProductSalesFile] = useState<any>();
  const [excelFile, setExcelFile] = useState<any>();
  const [fileType, setFileType] = useState<filesTypes>();

  // APIS
  const { mutate, isPending, error, status } = useUploadJourneyFileMutation();
  const { t } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");
  const {
    data: filesData,
    status: getFilesStatus,
    refetch,
    isFetching: isFetchingGetFiles,
  } = useGetJourneyFiles(
    (mode === "add" ? searchParams.get("journeyId") : id) ?? "",
    !!searchParams.get("journeyId") || !!id
  );

  const {
    mutate: mutateDeleteFile,
    status: deleteStatus,
    isPending: isPendingDelete,
  } = useDeleteJourneyFile();

  const handleFileUpload = (data: Blob, type: filesTypes) => {
    // Handle the file upload data for each XLSFileUploader instance
    const formDataToSend = convertObjectToFormData({
      journey_id: searchParams.get("journeyId") ?? "",
      type: type,
    });
    formDataToSend.append("file", data);
    setFileType(type);
    mutate(formDataToSend);
  };

  const handleDeleteFile = (id: string, type: filesTypes) => {
    setFileType(type);
    mutateDeleteFile(id);
  };

  // Uploading file error handling
  useEffect(() => {
    if (status === "success") {
      setFilesChanged(true);
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
      refetch();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
      setFilesChanged(true);
      if (fileType === "order_items") {
        setOrderItemsFile(undefined);
        setValue("orderByItems", "");
      } else if (fileType === "order_payments") {
        setOrderPaymentsFile(undefined);
        setValue("orderByPayment", "");
      } else if (fileType === "orders") {
        setOrdersFile(undefined);
        setValue("orders", "");
      } else if (fileType === "product_sales") {
        setProductSalesFile(undefined);
        setValue("productSales", "");
      } else if (fileType === "sale_payments") {
        setExcelFile(undefined);
        setValue("excelSales", "");
      }
    }
  }, [status]);

  // Deleting file error handling
  useEffect(() => {
    if (deleteStatus === "success") {
      setFilesChanged(true);
      if (fileType === "order_items") {
        setOrderItemsFile(undefined);
        setValue("orderByItems", "");
      } else if (fileType === "order_payments") {
        setOrderPaymentsFile(undefined);
        setValue("orderByPayment", "");
      } else if (fileType === "orders") {
        setOrdersFile(undefined);
        setValue("orders", "");
      } else if (fileType === "product_sales") {
        setProductSalesFile(undefined);
        setValue("productSales", "");
      } else if (fileType === "sale_payments") {
        setExcelFile(undefined);
        setValue("excelSales", "");
      }
    } else if (deleteStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: generalT("failedDeleteFile"),
        },
      ]);
    }
  }, [deleteStatus]);

  // Get files handling
  useEffect(() => {
    console.log("refetch", { getFilesStatus });
    if (
      getFilesStatus === "success"
      // &&
      // (mode === "edit" || (mode === "add" && isAutoImportedJourney))
    ) {
      console.log("success", { getFilesStatus });
      if (filesData?.data.journey_files.length > 0) {
        let order_items_file = filesData.data.journey_files.find(
          (file: any) => file.type === "order_items"
        );
        let order_payments_file = filesData.data.journey_files.find(
          (file: any) => file.type === "order_payments"
        );
        let orders_file = filesData.data.journey_files.find(
          (file: any) => file.type === "orders"
        );
        let product_sales_file = filesData.data.journey_files.find(
          // for manual
          (file: any) => file.type === "product_sales"
        );
        let excel_sales_file = filesData.data.journey_files.find(
          // for manual
          (file: any) => file.type === "sale_payments"
        );
        setOrderItemsFile(order_items_file);
        setOrderPaymentsFile(order_payments_file);
        setOrdersFile(orders_file);
        setProductSalesFile(product_sales_file);
        setExcelFile(excel_sales_file);
        // set form values
        setValue("orderByItems", order_items_file ? "exist" : "");
        setValue("orderByPayment", order_payments_file ? "exist" : "");
        setValue("orders", orders_file ? "exist" : "");
        setValue("productSales", product_sales_file ? "exist" : "");
        setValue("excelSales", excel_sales_file ? "exist" : "");
      } else {
        setOrderItemsFile(undefined);
        setOrderPaymentsFile(undefined);
        setOrdersFile(undefined);
        setProductSalesFile(undefined);
        setExcelFile(undefined);
      }
    }
  }, [isFetchingGetFiles]);

  const handleAppendNewAgg = () => {
    appendNewPaymentsAgg({
      aggregator_name: "",
      aggregator_fee: "",
      payments: [],
    });
  };

  const handleRemoveAgg = (index: number) => {
    console.log({ index });
    removeNewPaymentsAgg(index);
  };

  const isOutsideRange = (day: moment.Moment) => {
    if (journeysDateRange) {
      const startDate = moment(journeysDateRange?.start_date);
      const endDate = moment(journeysDateRange?.end_date).add(1, "day");
      return day.isBetween(startDate, endDate, null, "[]");
    }
  };

  const connectWhatsSupport = () => {
    const url = `https://wa.me/966507915074`;
    window.open(url, "_blank");
  };

  const handleDownloadTemplate = () => {
    const columns = ["SKU", "Type", "Name", "Qnty sold", "Order type"];
    downloadCsvTemplate(columns, "template.csv");
  };

  const onClickYesDiscountPaymentOption = () => {
    if (discountPaymentsFields.length === 0) {
      appendDiscountPayment({ payment_method_id: "", amount: "" });
    }
  };

  const onClickNoDiscountPaymentOption = () => {
    removeDiscountPayment();
  };

  const handleRemoveDiscountPayment = (index: number) => {
    removeDiscountPayment(index);
  };

  const handleAppendPayment = () => {
    appendDiscountPayment({
      payment_method_id: "",
      amount: "",
      name: "",
    });
  };

  const handlePaymentChange = (index: number, id: number) => {
    const selectedPayment = paymentsList.find((item) => item.id === id);
    console.log({ selectedPayment });
    setValue(`discountPayments.${index}.name`, selectedPayment?.name);
  };

  const selectedDiscountPaymentsIds = watch("discountPayments")?.map(
    (item) => item.payment_method_id
  );

  console.log({ productSalesFile });

  return (
    <div>
      <Box>
        <Typography fontSize={"26px"} fontWeight={600}>
          {t("calculateYourSales")}
        </Typography>
        <Typography color={"textSecondary"}>{t("letKnowYou")}</Typography>
        <YoutubeViewerBox
          videoId={
            watch("journey_type") === "foodics" ? "haCndSKn2rU" : "zVQdJwRhzgc"
          }
        />
        <Stack gap={2} alignItems={"flex-start"} width={"100%"}>
          <Controller
            name="journey_type"
            control={control}
            render={({ field }) => (
              <CustomRadioGroup
                options={[
                  {
                    label: t("manually"),
                    value: "manual",
                    icon: <img src={ManualIcon} alt="" />,
                    disabled: mode === "edit",
                  },
                  {
                    label: t("foodics"),
                    value: "foodics",
                    icon: <img src={FoodicsIcon} alt="" />,
                    disabled: mode === "edit",
                  },
                  {
                    label: t("excel"),
                    value: "excel",
                    icon: <img src={ExcelIcon} alt="" />,
                    disabled: mode === "edit",
                  },
                ]}
                {...field}
                onChange={(value) => {
                  setValue("journey_type", value as JourneyTypes);
                  removeDiscountPayment();
                  setValue("hasPaymentDiscount", "no");
                }}
              />
            )}
          />
          {/* Will be displayed if only we have journeys (added already) (not onboarding) */}
          {journeysDateRange?.start_date && journeysDateRange.end_date ? (
            <InfoBox
              text={`${t("yourLastTimeRange")} ${
                journeysDateRange?.start_date
              } ${t("to")} ${journeysDateRange?.end_date}. ${t(
                "pleaseEnsure"
              )} ${moment(journeysDateRange?.end_date)
                .add(1, "day")
                .format("YYYY-MM-DD")}.`}
            />
          ) : null}

          {watch("journey_type") === "foodics" ? (
            <>
              <Typography>{t("howToConnectWithFoodics")}</Typography>
              <CustomRadioGroup
                options={[
                  {
                    label: t("uploadFiles"),
                    description: t("youUploadFilesDesc"),
                    value: "uploadFiles",
                    icon: <></>,
                  },
                  {
                    label: t("connectWithFoodics"),
                    description: t("connectAutoDesc"),
                    value: "connectFoodics",
                    icon: <></>,
                  },
                ]}
                value={foodicsType}
                onChange={(value) => setFoodicsType(value)}
              />
              {foodicsType === "uploadFiles" ? (
                <>
                  {/* order Items File */}
                  {orderItemsFile ? (
                    <UploadedFile
                      label={t("goToFoodicsConsoleOrderItem")}
                      name={orderItemsFile?.file?.name}
                      size={formatBytes(orderItemsFile?.file?.size)}
                      handleDelete={() =>
                        handleDeleteFile(orderItemsFile.id, "order_items")
                      }
                      disabledDelete={
                        (fileType === "order_items" && isPendingDelete) ||
                        mode === "edit" ||
                        isAutoImportedJourney
                      }
                      isLoading={fileType === "order_items" && isPendingDelete}
                    />
                  ) : (
                    <FileUploader
                      label={t("goToFoodicsConsoleOrderItem")}
                      onFileUpload={(data) =>
                        handleFileUpload(data, "order_items")
                      }
                      id="orderByItems"
                      name="orderByItems"
                      control={control}
                      error={!!errors.orderByItems}
                      helperText={errors.orderByItems?.message}
                      isLoading={
                        (isPending && fileType === "order_items") ||
                        isFetchingGetFiles
                      }
                    />
                  )}
                  {/* order Payments File */}
                  {orderPaymentsFile ? (
                    <UploadedFile
                      label={t("goToFoodicsConsoleOrderPayments")}
                      name={orderPaymentsFile?.file?.name}
                      size={formatBytes(orderPaymentsFile?.file?.size)}
                      handleDelete={() =>
                        handleDeleteFile(orderPaymentsFile.id, "order_payments")
                      }
                      disabledDelete={
                        (fileType === "order_payments" && isPendingDelete) ||
                        mode === "edit" ||
                        isAutoImportedJourney
                      }
                      isLoading={
                        fileType === "order_payments" && isPendingDelete
                      }
                    />
                  ) : (
                    <FileUploader
                      label={t("goToFoodicsConsoleOrderPayments")}
                      onFileUpload={(data) =>
                        handleFileUpload(data, "order_payments")
                      }
                      id="orderByPayment"
                      name="orderByPayment"
                      control={control}
                      error={!!errors.orderByPayment}
                      helperText={errors.orderByPayment?.message}
                      isLoading={isPending && fileType === "order_payments"}
                    />
                  )}
                  {/* orders File */}
                  {ordersFile ? (
                    <UploadedFile
                      label={t("goToFoodicsConsoleOrders")}
                      name={ordersFile?.file?.name}
                      size={formatBytes(ordersFile?.file?.size)}
                      handleDelete={() =>
                        handleDeleteFile(ordersFile.id, "orders")
                      }
                      disabledDelete={
                        (fileType === "orders" && isPendingDelete) ||
                        watch("mode") === "edit" ||
                        isAutoImportedJourney
                      }
                      isLoading={fileType === "orders" && isPendingDelete}
                    />
                  ) : (
                    <FileUploader
                      label={t("goToFoodicsConsoleOrders")}
                      onFileUpload={(data) => handleFileUpload(data, "orders")}
                      id="orders"
                      name="orders"
                      control={control}
                      error={!!errors.orders}
                      helperText={errors.orders?.message}
                      isLoading={isPending && fileType === "orders"}
                    />
                  )}
                  <OtherSales
                    mode={mode}
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    otherRevenueFields={otherRevenueFields}
                    appendOtherRevenue={appendOtherRevenue}
                    removeOtherRevenue={removeOtherRevenue}
                    paymentsList={paymentsList}
                    journeyTaxPercentage={journeyTaxPercentage}
                    journeyTaxDivider={journeyTaxDivider}
                  />
                </>
              ) : (
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  width={"100%"}
                  pt={"20px"}
                >
                  <Typography>{t("enableSupportMessage")} </Typography>
                  <Link
                    onClick={connectWhatsSupport}
                    sx={{ cursor: "pointer" }}
                  >
                    {t("contactSupport")}
                  </Link>
                </Box>
              )}
            </>
          ) : watch("journey_type") === "excel" ? (
            <>
              <Divider sx={{ width: "100%" }} />
              <Controller
                name="total_order_quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    required
                    label={t("totalOrderQuantity")}
                    {...field}
                    error={!!errors.total_order_quantity}
                    helperText={errors.total_order_quantity?.message}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: field.value !== undefined,
                    }}
                    disabled={mode === "edit"}
                  />
                )}
              />
              {excelFile ? (
                <UploadedFile
                  label={t("excelFile")}
                  name={excelFile?.file?.name}
                  size={formatBytes(excelFile?.file?.size)}
                  handleDelete={() =>
                    handleDeleteFile(excelFile.id, "sale_payments")
                  }
                  disabledDelete={
                    (fileType === "sale_payments" && isPendingDelete) ||
                    mode === "edit" ||
                    isAutoImportedJourney
                  }
                  isLoading={fileType === "sale_payments" && isPendingDelete}
                />
              ) : (
                <FileUploader
                  label={t("excelFile")}
                  onFileUpload={(data) =>
                    handleFileUpload(data, "sale_payments")
                  }
                  id="excelSales"
                  name="excelSales"
                  control={control}
                  error={!!errors.excelSales}
                  helperText={errors.excelSales?.message}
                  isLoading={isPending && fileType === "sale_payments"}
                />
              )}

              <Box>
                <Typography mb={1}>
                  {t("toUploadYourSalesFromExcel")}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                  <Box
                    bgcolor={"#000"}
                    width={"4px"}
                    height={"4px"}
                    borderRadius={"50%"}
                  />
                  <Typography>{t("downloadOur")}</Typography>
                  <Link
                    color={"#1A1AE6"}
                    href={AWS_URLS.EXCEL_SALE_TEMPLATE}
                    sx={{ fontSize: "1rem", fontWeight: 500 }}
                  >
                    {t("template")}.
                  </Link>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                  <Box
                    bgcolor={"#000"}
                    width={"4px"}
                    height={"4px"}
                    borderRadius={"50%"}
                  />
                  <Typography>{t("fulfillRequiredColumns")}</Typography>
                </Stack>
              </Box>

              <OtherSales
                mode={mode}
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                otherRevenueFields={otherRevenueFields}
                appendOtherRevenue={appendOtherRevenue}
                removeOtherRevenue={removeOtherRevenue}
                paymentsList={paymentsList}
                journeyTaxPercentage={journeyTaxPercentage}
                journeyTaxDivider={journeyTaxDivider}
              />
            </>
          ) : (
            <>
              <Divider sx={{ width: "100%" }} />
              <Stack width={"100%"} gap={2}>
                <Controller
                  name="dateRange"
                  control={control}
                  defaultValue={{
                    start_date: "",
                    end_date: "",
                  }}
                  render={({ field }) => (
                    <DateRangeInput
                      disabled={mode === "edit"}
                      startDate={
                        field.value.start_date
                          ? moment(field.value.start_date)
                          : null
                      }
                      startDateId={`your_unique_start_date_id`}
                      endDate={
                        field.value.end_date
                          ? moment(field.value.end_date)
                          : null
                      }
                      endDateId={`your_unique_end_date_id`} // string;
                      onDatesChange={(arg: {
                        startDate: moment.Moment | null;
                        endDate: moment.Moment | null;
                      }) => {
                        setValue("dateRange", {
                          start_date: arg.startDate?.format(
                            "yyyy-MM-DD"
                          ) as string,
                          end_date: arg.endDate?.format("yyyy-MM-DD") as string,
                        });
                      }}
                      isOutsideRange={isOutsideRange}
                    />
                  )}
                />
                <FormHelperText
                  error
                  id="my-helper-text"
                  sx={{ mt: -1, px: 2 }}
                >
                  {errors.dateRange?.start_date?.message ??
                    errors.dateRange?.end_date?.message}
                </FormHelperText>

                {!watch("branchHasMenuFeature") ? (
                  <Controller
                    name="net_sales_with_tax"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        required
                        label={t("netSalesWithTaxExcludingDiscount")}
                        {...field}
                        error={!!errors.net_sales_with_tax}
                        helperText={errors.net_sales_with_tax?.message}
                        fullWidth
                        size="small"
                        InputLabelProps={{
                          shrink: field.value !== undefined,
                        }}
                      />
                    )}
                  />
                ) : null}
                <Controller
                  name="total_order_quantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      required
                      label={t("totalOrderQuantity")}
                      {...field}
                      error={!!errors.total_order_quantity}
                      helperText={errors.total_order_quantity?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
                <OtherSales
                  mode={mode}
                  control={control}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  otherRevenueFields={otherRevenueFields}
                  appendOtherRevenue={appendOtherRevenue}
                  removeOtherRevenue={removeOtherRevenue}
                  paymentsList={paymentsList}
                  journeyTaxPercentage={journeyTaxPercentage}
                  journeyTaxDivider={journeyTaxDivider}
                />
                {/* payment discount */}
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={{ xs: 1, sm: 1, md: 3 }}
                  flexWrap={"wrap"}
                  width={"100%"}
                >
                  <Typography color={"textSecondary"}>
                    {t("doYouHavePaymentDiscount")}
                  </Typography>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.hasPaymentDiscount}
                    sx={{
                      width: "fit-content",
                      ml: { xs: "auto", sm: "unset" },
                    }}
                  >
                    <Controller
                      name="hasPaymentDiscount"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          key={field.value}
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            value="yes"
                            control={
                              <Radio
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                              />
                            }
                            label={generalT("yes")}
                            sx={{ color: "#98A2B3" }}
                            onClick={onClickYesDiscountPaymentOption}
                          />
                          <FormControlLabel
                            value="no"
                            control={
                              <Radio
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                              />
                            }
                            label={generalT("no")}
                            sx={{ color: "#98A2B3" }}
                            onClick={onClickNoDiscountPaymentOption}
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                </Stack>

                {watch("hasPaymentDiscount") === "yes" ? (
                  <>
                    {/* amount */}
                    <Stack direction={"row"} alignItems={"flex-start"} gap={1}>
                      <Box sx={{ width: 260 }}>
                        <Controller
                          name="paymentDiscount"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              type="number"
                              fullWidth
                              label={t("totalPaymentDiscount")}
                              size="small"
                              {...field}
                              error={!!errors.paymentDiscount}
                              helperText={errors.paymentDiscount?.message}
                              InputLabelProps={{
                                shrink: field.value !== undefined,
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Stack>
                    {/* payments */}
                    {discountPaymentsFields.map(
                      (paymentField: any, index: number) => {
                        return (
                          <Stack
                            key={paymentField.id}
                            direction={"row"}
                            gap={1}
                            width={"100%"}
                          >
                            <FormControl
                              fullWidth
                              size="small"
                              error={
                                !!errors.discountPayments?.[index]
                                  ?.payment_method_id
                              }
                            >
                              <InputLabel id="demo-simple-select-label">
                                {generalT("paymentMethod")}
                              </InputLabel>
                              <Controller
                                name={`discountPayments.${index}.payment_method_id`}
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    key={field.value}
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label={generalT("paymentMethod")}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      handlePaymentChange(
                                        index,
                                        Number(e.target.value)
                                      );
                                    }}
                                  >
                                    {paymentsList
                                      ?.filter(
                                        (pay) =>
                                          pay.status &&
                                          pay.is_discount &&
                                          (!selectedDiscountPaymentsIds?.includes(
                                            `${pay.id}`
                                          ) ||
                                            `${pay.id}` === field.value)
                                      )
                                      .map(({ id, name }) => {
                                        return (
                                          <MenuItem key={id} value={`${id}`}>
                                            {name}
                                          </MenuItem>
                                        );
                                      })}
                                  </Select>
                                )}
                              />
                              <FormHelperText id="my-helper-text">
                                {
                                  errors.discountPayments?.[index]
                                    ?.payment_method_id?.message
                                }
                              </FormHelperText>
                            </FormControl>

                            <Controller
                              name={`discountPayments.${index}.amount`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={purchasingT("amount")}
                                  variant="outlined"
                                  {...field}
                                  error={
                                    !!errors.discountPayments?.[index]?.amount
                                  }
                                  helperText={
                                    errors.discountPayments?.[index]?.amount
                                      ?.message
                                  }
                                  fullWidth
                                  size="small"
                                  InputProps={{
                                    inputProps: { min: 0 },
                                  }}
                                />
                              )}
                            />

                            {mode === "add" &&
                              discountPaymentsFields.length > 1 && (
                                <IconButton
                                  onClick={() =>
                                    handleRemoveDiscountPayment(index)
                                  }
                                >
                                  <img src={DeleteIcon} alt="" />
                                </IconButton>
                              )}
                          </Stack>
                        );
                      }
                    )}
                    {watch("hasPaymentDiscount") === "yes" && (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<img src={AddMoreIcon} alt="" />}
                        onClick={handleAppendPayment}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {purchasingT("addAnotherMethod")}
                      </Button>
                    )}
                  </>
                ) : null}
                {/* was here */}
              </Stack>
            </>
          )}

          {/* product sales File */}
          {watch("branchHasMenuFeature") &&
            (watch("journey_type") === "manual" ||
              watch("journey_type") === "excel") && (
              <>
                {productSalesFile ? (
                  <UploadedFile
                    label={t("manualProductSales")}
                    name={productSalesFile?.file?.name}
                    size={formatBytes(productSalesFile?.file?.size)}
                    handleDelete={() =>
                      handleDeleteFile(productSalesFile.id, "product_sales")
                    }
                    disabledDelete={
                      (fileType === "product_sales" && isPendingDelete) ||
                      mode === "edit" ||
                      isAutoImportedJourney
                    }
                    isLoading={fileType === "product_sales" && isPendingDelete}
                  />
                ) : (
                  <FileUploader
                    label={t("manualProductSales")}
                    onFileUpload={(data) =>
                      handleFileUpload(data, "product_sales")
                    }
                    id="productSales"
                    name="productSales"
                    control={control}
                    error={!!errors.productSales}
                    helperText={errors.productSales?.message}
                    isLoading={isPending && fileType === "product_sales"}
                  />
                )}

                <Box>
                  <Typography mb={1}>
                    {t("toUploadYourSalesFromExcel")}
                  </Typography>
                  <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                    <Box
                      bgcolor={"#000"}
                      width={"4px"}
                      height={"4px"}
                      borderRadius={"50%"}
                    />
                    <Typography>{t("downloadOur")}</Typography>
                    <Link
                      color={"#1A1AE6"}
                      href={AWS_URLS.PRODUCT_SALE_TEMPLATE}
                      sx={{ fontSize: "1rem", fontWeight: 500 }}
                    >
                      {t("template")}.
                    </Link>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                    <Box
                      bgcolor={"#000"}
                      width={"4px"}
                      height={"4px"}
                      borderRadius={"50%"}
                    />
                    <Typography>{t("fulfillRequiredColumns")}</Typography>
                  </Stack>
                </Box>
              </>
            )}

          {/* For new payments found in foodics files */}
          {newPaymentsAggFields.length > 0 &&
            (watch("journey_type") === "foodics" ||
              watch("journey_type") === "excel") &&
            !filesChanged && (
              <Stack gap={2}>
                <InfoBox text={t("foundNewPayments")} />
                <Box>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    gap={{ xs: 1, sm: 1, md: 3 }}
                    flexWrap={"wrap"}
                    width={"100%"}
                  >
                    <Typography color={"textSecondary"}>
                      {t("isPaymentMethod")}
                    </Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.are_payments_new_agg}
                      sx={{
                        width: "fit-content",
                        ml: { xs: "auto", sm: "unset" },
                      }}
                    >
                      <Controller
                        name="are_payments_new_agg"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            key={field.value}
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                          >
                            <FormControlLabel
                              value={"yes"}
                              control={
                                <Radio
                                  icon={<CircleOutlinedIcon />}
                                  checkedIcon={<CheckCircleIcon />}
                                />
                              }
                              label={generalT("yes")}
                              sx={{ color: "#98A2B3" }}
                            />
                            <FormControlLabel
                              value="no"
                              control={
                                <Radio
                                  icon={<CircleOutlinedIcon />}
                                  checkedIcon={<CheckCircleIcon />}
                                />
                              }
                              label={generalT("no")}
                              sx={{ color: "#98A2B3" }}
                            />
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Stack>
                  <FormHelperText error id="my-helper-text" sx={{ px: 2 }}>
                    {errors.are_payments_new_agg?.message}
                  </FormHelperText>
                </Box>
                {watch("are_payments_new_agg") === "yes" &&
                  newPaymentsAggFields.map((fieldItem: any, index: number) => {
                    return (
                      <Stack key={fieldItem.key} gap={2}>
                        {/* name */}
                        <Controller
                          name={`new_payments_aggregators.${index}.aggregator_name`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              label={generalT("aggName")}
                              {...field}
                              error={
                                errors.new_payments_aggregators &&
                                !!errors.new_payments_aggregators[index]
                                  ?.aggregator_name
                              }
                              helperText={
                                errors.new_payments_aggregators?.[index]
                                  ?.aggregator_name?.message
                              }
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                        {/* fee */}
                        <Controller
                          name={`new_payments_aggregators.${index}.aggregator_fee`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              label={`${generalT("aggFee")} %`}
                              type="number"
                              {...field}
                              error={
                                errors.new_payments_aggregators &&
                                !!errors.new_payments_aggregators[index]
                                  ?.aggregator_fee
                              }
                              helperText={
                                errors.new_payments_aggregators?.[index]
                                  ?.aggregator_fee?.message
                              }
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                        {/* Related payments */}
                        <FormControl
                          fullWidth
                          size="small"
                          error={
                            errors.new_payments_aggregators &&
                            !!errors.new_payments_aggregators[index]?.payments
                          }
                        >
                          <InputLabel id="demo-multiple-checkbox-label">
                            {generalT("selectPayments")}
                          </InputLabel>
                          <Controller
                            name={`new_payments_aggregators.${index}.payments`}
                            control={control}
                            defaultValue={[]}
                            render={({ field }) => (
                              <Select
                                {...field}
                                labelId="demo-multiple-checkbox-label"
                                id="demo-multiple-checkbox"
                                multiple
                                input={
                                  <OutlinedInput
                                    label={generalT("selectPayments")}
                                  />
                                }
                                renderValue={(selected) =>
                                  selected
                                    .map(
                                      (selectedId: string) =>
                                        newPayments?.find(
                                          (payment: any) =>
                                            payment.id === +selectedId
                                        ).name
                                    )
                                    .join(", ")
                                }
                              >
                                {newPayments
                                  .filter((payment) => {
                                    return !watch(
                                      "new_payments_aggregators"
                                    ).some(
                                      (item, i) =>
                                        item?.payments?.includes(
                                          payment.id.toString()
                                        ) && i !== index
                                    );
                                  })
                                  ?.map((payment: any) => (
                                    <MenuItem
                                      key={payment.id}
                                      value={`${payment.id}`}
                                    >
                                      <Checkbox
                                        checked={
                                          field.value &&
                                          field.value.indexOf(`${payment.id}`) >
                                            -1
                                        }
                                      />
                                      {payment.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                            )}
                          />
                          <FormHelperText id="my-helper-text">
                            {
                              errors.new_payments_aggregators?.[index]?.payments
                                ?.message
                            }
                          </FormHelperText>
                        </FormControl>
                        {newPaymentsAggFields.length > 1 && (
                          <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => handleRemoveAgg(index)}
                            size="small"
                            sx={{ width: "fit-content", mx: "auto" }}
                          >
                            {generalT("delete")}
                          </Button>
                        )}
                        <Divider sx={{ width: "100%" }} />
                      </Stack>
                    );
                  })}
                <Button
                  sx={{ width: "180px", mx: "auto" }}
                  variant="contained"
                  onClick={handleAppendNewAgg}
                >
                  {generalT("addNewAgg")}
                </Button>
              </Stack>
            )}
        </Stack>
      </Box>
    </div>
  );
};

export default CalculateSales;
